import { useCallback, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { FOUNDATION_CSS_STYLE_TAG, FOUNDATION_LIQUID_TAG } from './foundation-preview-css';

const MIN_HEIGHT = 250;

type HtmlPreviewProps = {
  body: string;
  /** Applied to the iframe (e.g. `fs-mask` to keep rendered PII out of session replay). */
  className?: string;
};

/** The one `<meta>` a layout may declare; see {@link previewPurify}. */
const ALLOWED_META_NAME = 'viewport';

/**
 * Private DOMPurify instance. The hook below must not leak onto the shared
 * default instance, which other callers sanitize with.
 */
const previewPurify = DOMPurify(window);

// Allowing `<meta content>` is what makes a meta tag able to *do* anything, so
// drop every meta except the one we mean to keep. DOMPurify's own
// DOM-clobbering guard is not a substitute: it rejects `name="referrer"`
// because `'referrer' in document`, but it compares the raw value, so
// `name="Referrer"` sails through — and the Referrer-Policy spec matches meta
// names ASCII case-insensitively. A layout author could otherwise downgrade the
// policy to `unsafe-url` and have any remote image in the body leak the full
// Hydra URL, query string included, to a host they control.
previewPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName !== 'meta' || !(node instanceof Element)) {
    return;
  }
  if (node.getAttribute('name')?.toLowerCase() !== ALLOWED_META_NAME) {
    node.remove();
  }
});

/**
 * Sanitizes the layout-authored HTML before it reaches the iframe — the port
 * of Ember's preview whitelist (`cio/helpers/preview-whitelist`). DOMPurify
 * strips scripts, event handlers, and `javascript:`/`data:` URLs while
 * keeping the document structure email HTML needs (html/head/body, style,
 * link, tables).
 */
function sanitizeLayoutHtml(html: string): string {
  return previewPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    // Email layouts carry their styles in <style>/<link> tags (Ember's
    // whitelist allowed both); DOMPurify still applies its URL checks to the
    // link href. `meta` and the `content` attribute are on neither of
    // DOMPurify's default lists, so the viewport declaration was being dropped
    // outright. Ember kept every `meta`; the hook above narrows us to the
    // viewport one, which is the declaration the report was about. It is inert
    // either way — a nested browsing context takes its width from the iframe,
    // so the preview's `@media` rules resolve against the iframe width, not the
    // declared viewport — so this is output fidelity, not behavior.
    ADD_TAGS: ['link', 'meta'],
    ADD_ATTR: ['target', 'content'],
    // Ember stripped `meta[http-equiv=refresh]` by hand after sanitizing.
    // DOMPurify drops `http-equiv` today (it is on no default allowlist), but
    // allowing `content` is what would arm a meta refresh if that ever
    // changed, so forbid the attribute outright rather than rely on the
    // default.
    FORBID_ATTR: ['http-equiv'],
  });
}

/**
 * Expands `{% include_foundation_css %}` into the trusted Foundation
 * stylesheet, then sanitizes the result — the same swap Ember's preview does
 * (`ui` `app/components/html-preview/index.gts`), with a broader matcher and an
 * inline `<style>` payload where Ember uses an exact string and a `<link>`.
 * hydra's own `layout-preview-card.tsx` does the same swap. Splicing before
 * sanitization keeps the stylesheet where the layout put the tag, so it wins
 * (or loses) against the layout's own styles exactly as it will once the
 * backend expands the tag at send time.
 *
 * Only bodies carrying the tag get the stylesheet. Injecting it unconditionally
 * restyles emails that never opted in: Bee's drag-and-drop markup wraps rows in
 * the `row`/`columns` class names Foundation targets (it styles the singular
 * and plural alike), so `td.columns{padding-bottom:16px}` and
 * `td.columns table:not(.button){width:100%}` added phantom gaps and stretched
 * nested tables in previews of correctly-sent emails (CON-7965). A sent email
 * that really uses Foundation already carries the expanded stylesheet in its
 * stored body, so it still previews with the styles applied.
 */
function buildPreviewDoc(body: string): string {
  // Replacement passed as a function: a string replacement would expand `$&`,
  // `$'` and friends as substitution patterns. The bundled stylesheet has no
  // `$` today, but it is a third-party asset we swap on upgrade.
  return sanitizeLayoutHtml(body.replace(FOUNDATION_LIQUID_TAG, () => FOUNDATION_CSS_STYLE_TAG));
}

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function handleLinkClick(event: Event) {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const el = event.target as HTMLElement;
  const anchor = el.tagName === 'A' ? el : el.closest('a');

  if (anchor != null) {
    event.preventDefault();
    const href = anchor.getAttribute('href');
    if (href == null) {
      return;
    }
    // Defense in depth on top of sanitization: this handler runs in the
    // parent window, outside the iframe sandbox, so never hand `window.open`
    // a scheme like `javascript:` that the browser would evaluate.
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (SAFE_LINK_PROTOCOLS.has(url.protocol)) {
      // Sever the opener so the opened page can't reach back into the app
      // (reverse tab-nabbing).
      window.open(url.href, '_blank', 'noopener,noreferrer');
    }
  }
}

/**
 * Renders the layout body in an auto-growing iframe — port of Ember's
 * `HtmlPreview`.
 *
 * The body is sanitized (like Ember's whitelist pass) *and* loaded via
 * `srcDoc` in a sandbox without `allow-scripts`, so scripts can't run at the
 * platform level either. `allow-same-origin` (safe here since no scripts can
 * execute inside) lets the parent measure the content for auto-height and
 * intercept link clicks to open them in a new tab, matching Ember's
 * behavior.
 */
export function HtmlPreview({ body, className }: HtmlPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Sanitizing runs the whole document — plus the 22 KB Foundation stylesheet
  // for layouts that include it — through DOMPurify, so don't redo it for
  // re-renders that leave the body alone.
  const srcDoc = useMemo(() => buildPreviewDoc(body), [body]);

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (iframe == null || doc == null) {
      return;
    }
    // `documentElement.getBoundingClientRect().height` is fractional and
    // includes the <body> margin; Math.ceil closes Chrome's sub-1px phantom
    // scrollbar gap. Don't use `documentElement.scrollHeight` — it's floored
    // at the iframe's clientHeight so the frame never shrinks back. (Same
    // reasoning as the Ember component; see FLO-3453.)
    const preciseHeight =
      doc.documentElement == null ? 0 : Math.ceil(doc.documentElement.getBoundingClientRect().height);
    const height = Math.max(preciseHeight, doc.body?.scrollHeight ?? 0);
    iframe.style.height = `${Math.max(MIN_HEIGHT, height)}px`;
  }, []);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc == null) {
      return;
    }

    doc.removeEventListener('click', handleLinkClick);
    doc.addEventListener('click', handleLinkClick);

    // A single measurement at `load` undershoots: `loading="lazy"` images do
    // not delay the event, and late-resolving resources reflow the body after
    // it fires. Observing the body keeps the iframe sized to the final
    // laid-out content as that settles.
    resizeObserverRef.current?.disconnect();
    if (doc.body != null) {
      resizeObserverRef.current = new ResizeObserver(updateHeight);
      resizeObserverRef.current.observe(doc.body);
    }
    updateHeight();
  }, [updateHeight]);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="HTML preview"
      className={className}
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      style={{ width: '100%', border: 'none', height: `${MIN_HEIGHT}px` }}
    />
  );
}
